import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PredictiveAnalyticsPanel = ({ predictions, loading, onLoadPredictions }) => {
  const getLikelihoodColor = (likelihood) => {
    if (likelihood >= 80) return 'text-error';
    if (likelihood >= 60) return 'text-warning';
    if (likelihood >= 40) return 'text-primary';
    return 'text-success';
  };

  const getLikelihoodBg = (likelihood) => {
    if (likelihood >= 80) return 'bg-error/10';
    if (likelihood >= 60) return 'bg-warning/10';
    if (likelihood >= 40) return 'bg-primary/10';
    return 'bg-success/10';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Analyzing historical patterns...</p>
      </div>
    );
  }

  if (!predictions) {
    return (
      <div className="text-center py-12">
        <Icon name="TrendingUp" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
        <p className="text-sm text-muted-foreground mb-4">
          Generate AI-powered predictions based on historical compliance data
        </p>
        <Button variant="primary" onClick={onLoadPredictions} iconName="Sparkles">
          Generate Predictions
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Risk Score */}
      <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-heading font-semibold text-base text-foreground">Overall Risk Score</h4>
          <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
        </div>
        <div className="text-center">
          <div className={`text-5xl font-bold mb-2 ${
            predictions?.overallRiskScore >= 80 ? 'text-error' :
            predictions?.overallRiskScore >= 60 ? 'text-warning' : 'text-success'
          }`}>
            {predictions?.overallRiskScore}%
          </div>
          <p className="text-sm text-muted-foreground">Predicted Risk Level</p>
        </div>
      </div>
      {/* Key Risk Factors */}
      {predictions?.keyRiskFactors && predictions?.keyRiskFactors?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Key Risk Factors</h4>
          <ul className="space-y-2">
            {predictions?.keyRiskFactors?.map((factor, index) => (
              <li key={index} className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} color="var(--color-warning)" className="mt-0.5" />
                <span className="text-xs text-muted-foreground">{factor}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Predictions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-heading font-semibold text-base text-foreground">
            Predicted Violations ({predictions?.predictions?.length || 0})
          </h4>
          <Button variant="outline" size="sm" onClick={onLoadPredictions} iconName="RefreshCw">
            Refresh
          </Button>
        </div>

        <div className="space-y-3">
          {predictions?.predictions?.map((prediction, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <h5 className="font-heading font-semibold text-sm text-foreground mb-1">
                    {prediction?.violationType?.replace(/_/g, ' ')}
                  </h5>
                  <p className="text-xs text-muted-foreground">{prediction?.potentialImpact}</p>
                </div>
                <div className={`px-3 py-1 rounded-full ${
                  getLikelihoodBg(prediction?.likelihood)
                }`}>
                  <span className={`text-xs font-bold ${getLikelihoodColor(prediction?.likelihood)}`}>
                    {prediction?.likelihood}%
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Timeframe</p>
                  <p className="text-sm font-medium text-foreground">{prediction?.timeframe}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Likelihood</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          prediction?.likelihood >= 80 ? 'bg-error' :
                          prediction?.likelihood >= 60 ? 'bg-warning' :
                          prediction?.likelihood >= 40 ? 'bg-primary' : 'bg-success'
                        }`}
                        style={{ width: `${prediction?.likelihood}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Preventive Actions */}
              {prediction?.preventiveActions && prediction?.preventiveActions?.length > 0 && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs font-medium text-foreground mb-2">Preventive Actions</p>
                  <ul className="space-y-1">
                    {prediction?.preventiveActions?.map((action, actionIndex) => (
                      <li key={actionIndex} className="flex items-start gap-2">
                        <Icon name="CheckCircle2" size={14} color="var(--color-success)" className="mt-0.5" />
                        <span className="text-xs text-muted-foreground">{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Recommendations */}
      {predictions?.recommendations && predictions?.recommendations?.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Lightbulb" size={18} color="var(--color-primary)" />
            <h4 className="font-heading font-semibold text-sm text-foreground">AI Recommendations</h4>
          </div>
          <ul className="space-y-2">
            {predictions?.recommendations?.map((recommendation, index) => (
              <li key={index} className="flex items-start gap-2">
                <Icon name="ArrowRight" size={14} color="var(--color-primary)" className="mt-0.5" />
                <span className="text-xs text-muted-foreground">{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PredictiveAnalyticsPanel;